import { useMemo } from 'react';
import PageLoader from 'app/components/PageLoader';
import {
  isPackageRow,
  makePackageAwareColumns,
  PackageMembers,
} from 'app/components/PackageRow';
import PackageCollectPanel from 'app/components/PackageCollectPanel';
import PaginatedTable from 'app/components/table/PaginationTable';
import DeletionBucketSection from 'app/components/DeletionBucketSection';
import { CustomerVouchersHeader } from 'app/constants/table-header-constants';
import { ParseTableDate } from 'app/utils/Parsers';
import useCustomerVouchers from './useCustomerVouchers';
import Csv from 'assets/images/csv-icon.svg';
import Excel from 'assets/images/xls-icon.svg';
import CollectVoucher from './CollectVoucher';

// The expandable member list under a package row is kept but switched off
// (client request, 2026-10-12): package rows show no expand arrow.
const SHOW_PACKAGE_MEMBER_ROWS = false;

const CustomerVouchers = ({ latestDeletionDate, latestRestorationDate }) => {
  const {
    pageLoading,

    flatData,
    flatTotal,
    flatPageCount,
    fetchFlat,

    beforeData,
    beforeTotal,
    beforePageCount,
    fetchBefore,

    afterData,
    afterTotal,
    afterPageCount,
    fetchAfter,

    dataPerPage,

    startDownload,
    downloadInProgress,

    isFilterOpen,
    setIsFilterOpen,
    values,
    setValues,
    handleSubmit,
    handleNew,
    handleDelete,
    packageCollect,
  } = useCustomerVouchers(latestDeletionDate, latestRestorationDate);

  // One memo shared by all three bucket tables: PaginationTable memoises on the
  // columns array identity, so a fresh array per render would loop its fetch effect.
  const columns = useMemo(
    () => makePackageAwareColumns(CustomerVouchersHeader),
    []
  );
  // PaginationTable hands the row's original object straight to the sub-row renderer.
  const renderMembers = (row) => <PackageMembers vouchers={row?.vouchers} />;

  // Async export (job → poll → download), same flow and icon treatment as the
  // Bill History tab. Both formats offered, matching the reports' CSV/Excel
  // pair — the backend picks its branch off downloadType.
  const exportBar = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        marginBottom: 12,
      }}
    >
      <span
        className='link cursor-pointer download'
        style={
          downloadInProgress
            ? { opacity: 0.5, cursor: 'not-allowed' }
            : undefined
        }
        onClick={() => !downloadInProgress && startDownload('csv')}
      >
        <img alt='csv' src={Csv} />
      </span>
      <span
        className='link cursor-pointer download'
        style={
          downloadInProgress
            ? { opacity: 0.5, cursor: 'not-allowed' }
            : undefined
        }
        onClick={() => !downloadInProgress && startDownload('excel')}
      >
        <img alt='excel' src={Excel} />
      </span>
      {downloadInProgress && (
        <span
          style={{
            marginLeft: 8,
            fontSize: '0.85rem',
            color: '#666',
            fontStyle: 'italic',
          }}
        >
          Generating report…
        </span>
      )}
    </div>
  );

  if (!latestDeletionDate) {
    return (
      <>
        <div className='cms-content__container'>
          {exportBar}
          <PaginatedTable
            columns={columns}
            data={flatData}
            currentPage={1}
            dataPerPage={dataPerPage}
            totalNumber={flatTotal}
            pageCount={flatPageCount}
            pageLoading={pageLoading}
            handleNew={handleNew}
            handleDelete={handleDelete}
            canExpandRow={SHOW_PACKAGE_MEMBER_ROWS ? isPackageRow : undefined}
            renderSubRow={SHOW_PACKAGE_MEMBER_ROWS ? renderMembers : undefined}
            fetchData={fetchFlat}
          />
        </div>

        {isFilterOpen && (
          <CollectVoucher
            isFilterOpen={isFilterOpen}
            setIsFilterOpen={setIsFilterOpen}
            onSubmit={handleSubmit}
            values={values}
            setValues={setValues}
          />
        )}

        {/* Page-level sibling: both drawers render their own <form>. */}
        <PackageCollectPanel
          lines={packageCollect.lines}
          phoneNumber={packageCollect.phoneNumber}
          isOpen={packageCollect.isOpen}
          setIsOpen={packageCollect.setIsOpen}
          onLineChange={packageCollect.handleLineChange}
          onSubmit={packageCollect.handleSubmit}
        />

        {pageLoading === true && <PageLoader pageLoading={pageLoading} />}
      </>
    );
  }

  const boundaryFormatted = ParseTableDate(latestDeletionDate);
  const restorationFormatted = latestRestorationDate
    ? ParseTableDate(latestRestorationDate)
    : undefined;
  return (
    <>
      <div className='cms-content__container'>
        {exportBar}
        <DeletionBucketSection
          title='Account restored'
          captionDate={restorationFormatted}
          captionPrefix='Account restored on'
          isEmpty={afterData.length === 0}
        >
          <PaginatedTable
            columns={columns}
            data={afterData}
            currentPage={1}
            dataPerPage={dataPerPage}
            totalNumber={afterTotal}
            pageCount={afterPageCount}
            pageLoading={pageLoading}
            handleNew={handleNew}
            handleDelete={handleDelete}
            canExpandRow={SHOW_PACKAGE_MEMBER_ROWS ? isPackageRow : undefined}
            renderSubRow={SHOW_PACKAGE_MEMBER_ROWS ? renderMembers : undefined}
            fetchData={fetchAfter}
          />
        </DeletionBucketSection>
        <DeletionBucketSection
          title='Account deleted'
          captionDate={boundaryFormatted}
          isEmpty={beforeData.length === 0}
        >
          <PaginatedTable
            columns={columns}
            data={beforeData}
            currentPage={1}
            dataPerPage={dataPerPage}
            totalNumber={beforeTotal}
            pageCount={beforePageCount}
            pageLoading={pageLoading}
            handleNew={handleNew}
            handleDelete={handleDelete}
            canExpandRow={SHOW_PACKAGE_MEMBER_ROWS ? isPackageRow : undefined}
            renderSubRow={SHOW_PACKAGE_MEMBER_ROWS ? renderMembers : undefined}
            fetchData={fetchBefore}
          />
        </DeletionBucketSection>
      </div>

      {isFilterOpen && (
        <CollectVoucher
          isFilterOpen={isFilterOpen}
          setIsFilterOpen={setIsFilterOpen}
          onSubmit={handleSubmit}
          values={values}
          setValues={setValues}
        />
      )}

      {/* Page-level sibling: both drawers render their own <form>. */}
      <PackageCollectPanel
        lines={packageCollect.lines}
        phoneNumber={packageCollect.phoneNumber}
        isOpen={packageCollect.isOpen}
        setIsOpen={packageCollect.setIsOpen}
        onLineChange={packageCollect.handleLineChange}
        onSubmit={packageCollect.handleSubmit}
      />

      {pageLoading === true && <PageLoader pageLoading={pageLoading} />}
    </>
  );
};

export default CustomerVouchers;
